import { HttpClient } from '@adapters/HttpClient';
import { MismatchFilters, MismatchesResponse } from '../models';

export class MismatchesApiAdapter {
    constructor(private httpClient: HttpClient) {}

    /**
     * Obtiene listado de descuadres con filtros y paginación
     * GET /api/v1/ledger/mismatches
     *
     * Query Parameters:
     * - client_id: Filtrar por cliente
     * - shop_id: Filtrar por tienda
     * - courier_id: Filtrar por courier
     * - ref: Buscar por referencia de pedido
     * - tracking_number: Buscar por tracking
     * - mismatch_type: Tipo de descuadre
     * - date_from: Fecha desde (Y-m-d)
     * - date_to: Fecha hasta (Y-m-d)
     * - per_page: Registros por página (default: 15)
     * - page: Número de página (default: 1)
     * - sort_by: Campo para ordenar
     * - sort_direction: asc o desc
     */
    async getMismatches(filters: MismatchFilters = {}): Promise<MismatchesResponse> {
        const {
            client_id,
            shop_id,
            courier_id,
            ref,
            tracking_number,
            mismatch_type,
            date_from,
            date_to,
            per_page = 15,
            page = 1,
            sort_by,
            sort_direction = 'desc',
        } = filters;

        const params: Record<string, any> = {
            page,
            per_page,
            ...(client_id ? { client_id } : {}),
            ...(shop_id ? { shop_id } : {}),
            ...(courier_id ? { courier_id } : {}),
            ...(ref ? { ref } : {}),
            ...(tracking_number ? { tracking_number } : {}),
            ...(mismatch_type ? { mismatch_type } : {}),
            ...(date_from ? { date_from } : {}),
            ...(date_to ? { date_to } : {}),
            ...(sort_by ? { sort_by } : {}),
            ...(sort_direction ? { sort_direction } : {}),
        };

        const response = await this.httpClient.get<MismatchesResponse>(
            '/ledger/mismatches',
            params,
            {},
        );

        return response;
    }

    /**
     * Exporta descuadres a Excel
     * POST /api/v1/ledger/mismatches/export
     *
     * Query Parameters: Los mismos filtros que getMismatches (sin paginación)
     * Body: { columns: string[] }
     * Returns: { url_file: string }
     */
    async exportMismatches(
        filters: Partial<MismatchFilters> = {},
        columns: string[] = []
    ): Promise<{ url_file: string }> {
        const {
            client_id,
            shop_id,
            courier_id,
            ref,
            tracking_number,
            mismatch_type,
            date_from,
            date_to,
            sort_by,
            sort_direction,
        } = filters;

        // Construir query params (sin paginación)
        const queryParams: Record<string, string> = {};
        if (client_id) queryParams.client_id = String(client_id);
        if (shop_id) queryParams.shop_id = String(shop_id);
        if (courier_id) queryParams.courier_id = String(courier_id);
        if (ref) queryParams.ref = ref;
        if (tracking_number) queryParams.tracking_number = tracking_number;
        if (mismatch_type) queryParams.mismatch_type = mismatch_type;
        if (date_from) queryParams.date_from = date_from;
        if (date_to) queryParams.date_to = date_to;
        if (sort_by) queryParams.sort_by = sort_by;
        if (sort_direction) queryParams.sort_direction = sort_direction;

        // Columnas por defecto si no se especifican
        const exportColumns = columns.length > 0 ? columns : [
            'order_id',
            'ref',
            'tracking_number',
            'shop_name',
            'courier_name',
            'order_total',
            'courier_total',
            'client_total',
            'balance',
            'mismatch_type',
            'last_movement',
            'delivered_at',
        ];

        // Body con columnas
        const body = { columns: exportColumns };

        // Construir URL con query params
        const queryString = new URLSearchParams(queryParams).toString();
        const url = `/ledger/mismatches/export${queryString ? `?${queryString}` : ''}`;

        const response = await this.httpClient.post<{ url_file: string }>(
            url,
            body,
        );

        return response;
    }
}

